import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import * as membershipsService from '../services/memberships';
import { groupKeys } from './useGroups';
import type {
  BulkAddUserGroupsInput,
  SetUserGroupsInput,
} from '../types/memberships.types';
import type { GroupMembershipBadgeRow } from '../types/database';

export const membershipKeys = {
  all: ['memberships'] as const,
  forUser: (userId: string) => [...membershipKeys.all, 'user', userId] as const,
  forUserBatch: (userIds: string[]) =>
    [...membershipKeys.all, 'batch', [...userIds].sort()] as const,
  groupMembers: (groupId: string) =>
    [...membershipKeys.all, 'group', groupId] as const,
};

export function useUserGroupMemberships(userId: string | null | undefined) {
  return useQuery({
    queryKey: membershipKeys.forUser(userId ?? ''),
    queryFn: () =>
      membershipsService.listUserGroupMemberships(userId as string),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

export function useSetUserGroupMemberships() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetUserGroupsInput) =>
      membershipsService.setUserGroupMemberships(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: membershipKeys.forUser(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: membershipKeys.all });
      // Member counts changed on touched groups.
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useBulkAddUserGroupMemberships() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BulkAddUserGroupsInput) =>
      membershipsService.bulkAddUserGroupMemberships(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: membershipKeys.all });
      for (const userId of variables.userIds) {
        queryClient.invalidateQueries({
          queryKey: membershipKeys.forUser(userId),
        });
      }
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useUsersGroupMemberships(userIds: string[]) {
  const stableIds = useMemo(() => [...userIds].sort(), [userIds]);
  const query = useQuery({
    queryKey: membershipKeys.forUserBatch(stableIds),
    queryFn: () => membershipsService.listGroupMembershipsForUsers(stableIds),
    enabled: stableIds.length > 0,
    staleTime: 60 * 1000,
  });

  const byUser = useMemo(() => {
    const map = new Map<string, GroupMembershipBadgeRow[]>();
    for (const row of query.data ?? []) {
      const list = map.get(row.user_id) ?? [];
      list.push(row);
      map.set(row.user_id, list);
    }
    return map;
  }, [query.data]);

  return { ...query, byUser };
}

export function useGroupMembers(groupId: string | null | undefined) {
  return useQuery({
    queryKey: membershipKeys.groupMembers(groupId ?? ''),
    queryFn: () => membershipsService.listGroupMembers(groupId as string),
    enabled: !!groupId,
    staleTime: 60 * 1000,
  });
}
